import { IncomeTypes, useTenantSettings } from "@nestoca/multi-tenant";
import { useTranslation } from "next-i18next";

import { EMPLOYMENT_TYPE_OPTIONS } from "./applicant.const";

type Income = typeof IncomeTypes;

export type IncomeType = Income[keyof Income];

type IncomeMap = Record<
  IncomeType,
  {
    id: IncomeType;
    labelKey: string;
  }
>;

export const INCOME_MAP: IncomeMap = {
  [IncomeTypes.BONUS]: {
    id: IncomeTypes.BONUS,
    labelKey: "form:incomeType.bonus",
  },
  [IncomeTypes.COMMISSIONS]: {
    id: IncomeTypes.COMMISSIONS,
    labelKey: "form:incomeType.commission",
  },
  [IncomeTypes.HOURLY]: {
    id: IncomeTypes.HOURLY,
    labelKey: "form:incomeType.hourly",
  },
  [IncomeTypes.HOURLY_COMMISSIONS]: {
    id: IncomeTypes.HOURLY_COMMISSIONS,
    labelKey: "form:incomeType.hourlyCommissions",
  },
  [IncomeTypes.NONE]: {
    id: IncomeTypes.NONE,
    labelKey: "form:incomeType.noIncome",
  },
  [IncomeTypes.ON_CONTRACT]: {
    id: IncomeTypes.ON_CONTRACT,
    labelKey: "form:incomeType.onContract",
  },
  [IncomeTypes.OTHER]: {
    id: IncomeTypes.OTHER,
    labelKey: "Other",
  },
  [IncomeTypes.OVERTIME_AND_PREMIUMS]: {
    id: IncomeTypes.OVERTIME_AND_PREMIUMS,
    labelKey: "form:incomeType.overtimePremiums",
  },
  [IncomeTypes.PENSION]: {
    id: IncomeTypes.PENSION,
    labelKey: "form:incomeType.pension",
  },
  [IncomeTypes.SALARIED]: {
    id: IncomeTypes.SALARIED,
    labelKey: "form:incomeType.salaried",
  },
  [IncomeTypes.SEASONAL]: {
    id: IncomeTypes.SEASONAL,
    labelKey: "form:incomeType.seasonal",
  },
  [IncomeTypes.SELF_EMPLOYED]: {
    id: IncomeTypes.SELF_EMPLOYED,
    labelKey: "form:incomeType.selfEmployed",
  },
  [IncomeTypes.SELF_EMPLOYED_STATED]: {
    id: IncomeTypes.SELF_EMPLOYED_STATED,
    labelKey: "form:incomeType.selfEmployedStated",
  },
  [IncomeTypes.FLUCTUATING_IRREGULAR]: {
    id: IncomeTypes.FLUCTUATING_IRREGULAR,
    labelKey: "form:incomeType.irregular",
  },
  [IncomeTypes.EMPLOYED_BY_FAMILY]: {
    id: IncomeTypes.EMPLOYED_BY_FAMILY,
    labelKey: "form:incomeType.familyEmployed",
  },
  [IncomeTypes.GOV_CHILDCARE_SOCIAL_ASSIST]: {
    id: IncomeTypes.GOV_CHILDCARE_SOCIAL_ASSIST,
    labelKey: "form:incomeType.govChildCareSocialAssist",
  },
  [IncomeTypes.NORTHERN_ALLOWANCE]: {
    id: IncomeTypes.NORTHERN_ALLOWANCE,
    labelKey: "form:incomeType.northernAllowance",
  },
  [IncomeTypes.FOSTER_CARE_INCOME]: {
    id: IncomeTypes.FOSTER_CARE_INCOME,
    labelKey: "form:incomeType.fosterCare",
  },
  [IncomeTypes.INVESTMENT_INCOME]: {
    id: IncomeTypes.INVESTMENT_INCOME,
    labelKey: "form:incomeType.investmentIncome",
  },
  [IncomeTypes.LEAVE_OF_ABSENCE]: {
    id: IncomeTypes.LEAVE_OF_ABSENCE,
    labelKey: "form:incomeType.leaveOfAbsence",
  },
  [IncomeTypes.NON_CANADIAN_CURRENCY]: {
    id: IncomeTypes.NON_CANADIAN_CURRENCY,
    labelKey: "form:incomeType.nonCanadianCurrency",
  },
  [IncomeTypes.NON_TAXABLE_INCOME]: {
    id: IncomeTypes.NON_TAXABLE_INCOME,
    labelKey: "form:incomeType.nonTaxableIncome",
  },
  [IncomeTypes.PENSION_DISABILITY]: {
    id: IncomeTypes.PENSION_DISABILITY,
    labelKey: "form:incomeType.pensionInvalidity",
  },
  [IncomeTypes.RENTAL_INCOME]: {
    id: IncomeTypes.RENTAL_INCOME,
    labelKey: "form:incomeType.rentalIncome",
  },
  [IncomeTypes.SPOUSAL_CHILD_SUPPORT]: {
    id: IncomeTypes.SPOUSAL_CHILD_SUPPORT,
    labelKey: "form:incomeType.spousalChildSupport",
  },
};

export const useIncomeOptions = (): {
  value: IncomeType;
  label: string;
}[] => {
  const { t } = useTranslation();

  const {
    typesOverride: {
      settings: { incomeTypes },
    },
  } = useTenantSettings();

  return incomeTypes
    .filter((typeId) => !!INCOME_MAP[typeId])
    .map((typeId) => ({
      value: typeId,
      label: t(INCOME_MAP[typeId]?.labelKey || ""),
    }));
};

export const useEmploymentTypeOptions = (): {
  value: string;
  label: string;
}[] => {
  const { t } = useTranslation();

  const {
    typesOverride: { settings: { employmentTypes } },
  } = useTenantSettings();

  const options = employmentTypes
    ? EMPLOYMENT_TYPE_OPTIONS.filter((o) => employmentTypes.includes(o.value))
    : EMPLOYMENT_TYPE_OPTIONS;

  return options.map((o) => ({ value: o.value, label: t(o.label) }));
};
